import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  Banknote,
  CopyCheck,
  Download,
  Gauge,
  Percent,
  Receipt as ReceiptIcon,
  RefreshCw,
  ScanLine,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/layout/AdminShell";
import EmptyState from "../../components/EmptyState";
import KpiCard from "../../components/KpiCard";
import QuotaBanner from "../../components/QuotaBanner";
import ReceiptDetailDrawer from "../../components/ReceiptDetailDrawer";
import StatusBadge, { ConfidenceMeter } from "../../components/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { DashboardApi, DuplicatesApi, ReceiptsApi, errorMessage } from "../../lib/api";
import { formatDate, formatMoney, formatNumber, isoDaysAgo, relativeTime } from "../../lib/format";
import { planName, seatLimitLabel } from "../../lib/plans";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useI18n } from "../../i18n";

const RANGES = [
  { key: "30", labelKey: "dash.range30", days: 30 },
  { key: "90", labelKey: "dash.range90", days: 90 },
  { key: "365", labelKey: "dash.range365", days: 365 },
  { key: "all", labelKey: "dash.rangeAll", days: null },
];

const chartTooltip = (currency) => ({
  contentStyle: {
    borderRadius: 12,
    border: "1px solid hsl(var(--border))",
    background: "hsl(var(--card))",
    fontSize: 12,
    color: "hsl(var(--card-foreground))",
  },
  formatter: (value, name) => [formatMoney(value, currency), name],
});

const Dashboard = () => {
  const { t } = useI18n();
  const { currency, categories, isAdmin, isAccountingFirm } = useAuth();
  const [range, setRange] = useState("365");
  const [summary, setSummary] = useState(null);
  const [feed, setFeed] = useState([]);
  const [feedMeta, setFeedMeta] = useState({ in_progress: 0, needs_review: 0, suspected_duplicates: 0 });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const seenIds = useRef(new Set());
  const [newIds, setNewIds] = useState(new Set());
  const [duplicateCount, setDuplicateCount] = useState(0);

  const params = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    return r?.days ? { date_from: isoDaysAgo(r.days) } : {};
  }, [range]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await DashboardApi.summary(params);
      setSummary(data);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load the dashboard"));
    } finally {
      setLoading(false);
    }
  }, [params]);

  const loadDuplicates = useCallback(() => {
    if (!isAdmin) return;
    DuplicatesApi.count()
      .then((d) => setDuplicateCount(d.count || 0))
      .catch(() => setDuplicateCount(0));
  }, [isAdmin]);

  const loadFeed = useCallback(async (markNew = true) => {
    try {
      const data = await ReceiptsApi.feed();
      const items = data.receipts || [];
      if (markNew && seenIds.current.size) {
        const fresh = items.filter((r) => !seenIds.current.has(r.id)).map((r) => r.id);
        if (fresh.length) {
          setNewIds(new Set(fresh));
          setTimeout(() => setNewIds(new Set()), 2000);
        }
      }
      items.forEach((r) => seenIds.current.add(r.id));
      setFeed(items);
      setFeedMeta({
        in_progress: data.in_progress || 0,
        needs_review: data.needs_review || 0,
        suspected_duplicates: data.suspected_duplicates || 0,
      });
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadFeed(false);
    loadDuplicates();
  }, [loadFeed, loadDuplicates]);

  // live feed polling — faster while receipts are still being scanned
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (document.hidden || cancelled) return;
      const data = await loadFeed(true);
      loadDuplicates();
      if (data && (data.in_progress || 0) > 0) loadSummary();
    };
    const interval = setInterval(tick, feedMeta.in_progress > 0 ? 3000 : 8000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [loadFeed, loadSummary, loadDuplicates, feedMeta.in_progress]);

  const suspectedDuplicates = Math.max(
    duplicateCount,
    feedMeta.suspected_duplicates || 0,
    summary?.kpis?.suspected_duplicates || 0
  );
  const kpis = summary?.kpis || {};
  const usage = summary?.usage || {};
  const scanPct = usage.included_scans
    ? Math.min(100, Math.round((usage.scans_used / usage.included_scans) * 100))
    : 0;

  const refresh = () => {
    setLoading(true);
    loadSummary();
    loadFeed(false);
  };

  return (
    <AdminShell
      title={t("dash.title")}
      subtitle={t("dash.sub")}
      actions={
        <>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="dashboard-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => (
                <SelectItem key={r.key} value={r.key}>
                  {t(r.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="dashboard-refresh-button">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Link to="/app/scan">
            <Button size="sm" data-testid="dashboard-scan-button">
              <ScanLine className="mr-2 h-4 w-4" /> {t("dash.scanCta")}
            </Button>
          </Link>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 md:gap-4">
        {loading && !summary ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-xl" />)
        ) : (
          <>
            <KpiCard
              label={t("dash.kpiExpenses")}
              value={formatMoney(kpis.gross_total, currency)}
              sub={`${kpis.receipt_count || 0} ${t("dash.kpiExpensesSub")}`}
              icon={Banknote}
              tone="primary"
              testId="kpi-gross"
              hint={t("dash.kpiExpensesHint")}
            />
            <KpiCard
              label={t("dash.kpiVat")}
              value={formatMoney(kpis.vat_total, currency)}
              sub={`${t("dash.netBase")} ${formatMoney(kpis.net_total, currency)}`}
              icon={Percent}
              tone="blue"
              testId="kpi-vat"
              hint={t("dash.kpiVatHint")}
            />
            {!isAccountingFirm ? (
            <KpiCard
              label={t("dash.kpiRefunds")}
              value={formatMoney(kpis.refund_total, currency)}
              sub={t("dash.kpiRefundsSub")}
              icon={Wallet}
              tone="primary"
              testId="kpi-refunds"
              hint={t("dash.kpiRefundsHint")}
            />
            ) : null}
            <KpiCard
              label={isAccountingFirm ? t("dash.kpiUnverified") : t("dash.kpiReview")}
              value={formatNumber(kpis.needs_review || 0, 0)}
              sub={`${kpis.in_progress || 0} ${t("dash.scanning")} · ${kpis.failed || 0} ${t("dash.failed")}`}
              icon={AlertTriangle}
              tone="amber"
              testId="kpi-review"
              hint={t("dash.kpiReviewHint")}
            />
            <KpiCard
              label={t("dash.kpiScans")}
              value={`${formatNumber(usage.scans_used || 0, 0)} / ${formatNumber(
                usage.included_scans || 0,
                0
              )}`}
              sub={`${planName(t, usage.plan, usage.plan_name)} · ${usage.seats_used || 0}/${seatLimitLabel(t, usage.max_seats, usage.unlimited_seats)} ${t("common.seats").toLowerCase()}`}
              icon={Gauge}
              testId="kpi-usage"
              hint={t("dash.kpiScansHint")}
            />
          </>
        )}
      </div>

      {isAdmin ? <QuotaBanner quota={usage} className="mt-4" /> : null}

      {isAdmin && suspectedDuplicates > 0 ? (
        <div
          className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
          data-testid="duplicates-warning"
        >
          <div className="flex items-center gap-2">
            <CopyCheck className="h-4 w-4 text-amber-700" />
            <p className="text-sm font-medium text-amber-900">
              {t("dash.duplicatesWarning", { count: suspectedDuplicates })}
            </p>
          </div>
          <Link to="/app/duplicates">
            <Button size="sm" data-testid="duplicates-review-button">
              {t("dash.reviewDuplicates")}
            </Button>
          </Link>
        </div>
      ) : null}

      <div className="mt-5 grid gap-5 xl:grid-cols-12">
        <section className="min-w-0 xl:col-span-7">
          <div className="ll-card overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <div>
                <h2 className="font-display text-base font-semibold">{t("dash.feedTitle")}</h2>
                <p className="text-xs text-muted-foreground">
                  {feedMeta.in_progress > 0
                    ? `${feedMeta.in_progress} · ${t("dash.feedActive")}`
                    : t("dash.feedIdle")}
                </p>
              </div>
              <Link to="/app/receipts">
                <Button variant="ghost" size="sm" data-testid="feed-view-all">
                  {t("dash.viewAll")}
                </Button>
              </Link>
            </div>
            {feed.length === 0 ? (
              <EmptyState
                className="border-0"
                icon={ReceiptIcon}
                title={t("dash.emptyTitle")}
                description={t("dash.emptyBody")}
                actionLabel={t("receipts.scanCta")}
                onAction={() => {
                  window.location.href = "/app/scan";
                }}
              />
            ) : (
              <div className="ll-scroll overflow-x-auto">
                <Table data-testid="receipt-feed-table">
                  <TableHeader className="bg-muted/40">
                    <TableRow>
                      <TableHead>{t("dash.vendorEmployee")}</TableHead>
                      <TableHead>{t("common.date")}</TableHead>
                      <TableHead className="text-right">{t("common.gross")}</TableHead>
                      <TableHead className="text-right">{t("common.vat")}</TableHead>
                      <TableHead className="text-right">{t("common.refund")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feed.map((r) => (
                      <TableRow
                        key={r.id}
                        onClick={() => setOpenId(r.id)}
                        onKeyDown={(e) => e.key === "Enter" && setOpenId(r.id)}
                        tabIndex={0}
                        className={`cursor-pointer hover:bg-muted/50 ${
                          newIds.has(r.id) ? "ll-row-new" : ""
                        }`}
                        data-testid="receipt-feed-row"
                      >
                        <TableCell className="max-w-[220px]">
                          <div className="truncate font-medium">{r.vendor_name || r.filename || "Receipt"}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {r.user_name} · {relativeTime(r.created_at)}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {r.issue_date ? formatDate(r.issue_date) : "—"}
                        </TableCell>
                        <TableCell className="ll-num text-right font-medium" data-testid="receipt-gross-amount">
                          {r.gross_total != null ? formatMoney(r.gross_total, r.currency || currency) : "—"}
                        </TableCell>
                        <TableCell className="ll-num text-right">
                          {r.vat_total != null ? formatMoney(r.vat_total, r.currency || currency) : "—"}
                        </TableCell>
                        <TableCell className="ll-num text-right text-primary">
                          {r.refund?.amount ? formatMoney(r.refund.amount, r.currency || currency) : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <StatusBadge status={r.status} />
                            {r.confidence != null && ["parsed", "needs_review", "approved"].includes(r.status) ? (
                              <ConfidenceMeter value={r.confidence} />
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </section>

        <section className="min-w-0 space-y-5 xl:col-span-5">
          <div className="ll-card p-4">
            <h2 className="font-display text-base font-semibold">{t("dash.vatByRate")}</h2>
            <p className="text-xs text-muted-foreground">{t("dash.vatByRateSub")}</p>
            <div className="mt-3 h-[190px]">
              {summary?.vat_by_rate?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.vat_by_rate.map((v) => ({ ...v, name: `${v.rate}%` }))}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={54} />
                    <RTooltip {...chartTooltip(currency)} />
                    <Bar dataKey="net" name={t("dash.netBase")} fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="vat" name={t("common.vat")} fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {t("dash.noVatData")}
                </div>
              )}
            </div>
          </div>

          <div className="ll-card p-4">
            <h2 className="font-display text-base font-semibold">{t("dash.spendByCategory")}</h2>
            <div className="mt-3 h-[210px]">
              {summary?.by_category?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={summary.by_category.slice(0, 8)}
                    layout="vertical"
                    margin={{ left: 8, right: 12 }}
                  >
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={110}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    />
                    <RTooltip {...chartTooltip(currency)} />
                    <Bar dataKey="gross" name={t("common.gross")} radius={[0, 4, 4, 0]}>
                      {summary.by_category.slice(0, 8).map((c) => (
                        <Cell key={c.category} fill={c.color || "hsl(var(--chart-1))"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {t("dash.noCategoryData")}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-12">
        <div className="ll-card min-w-0 p-4 xl:col-span-7">
          <h2 className="font-display text-base font-semibold">{t("dash.spendOverTime")}</h2>
          <div className="mt-3 h-[220px]">
            {summary?.over_time?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={summary.over_time}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} width={54} />
                  <RTooltip {...chartTooltip(currency)} />
                  <Area
                    type="monotone"
                    dataKey="gross"
                    name={t("common.gross")}
                    stroke="hsl(var(--chart-1))"
                    fill="hsl(var(--chart-1))"
                    fillOpacity={0.14}
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="vat"
                    name={t("common.vat")}
                    stroke="hsl(var(--chart-2))"
                    fill="hsl(var(--chart-2))"
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t("dash.noReceiptsPeriod")}
              </div>
            )}
          </div>
        </div>

        {!isAccountingFirm ? (
        <div className="ll-card min-w-0 overflow-hidden xl:col-span-5">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="font-display text-base font-semibold">{t("dash.refundsByEmployee")}</h2>
            <Link to="/app/reports">
              <Button variant="ghost" size="sm">
                <Download className="mr-2 h-3.5 w-3.5" /> {t("common.export")}
              </Button>
            </Link>
          </div>
          {summary?.by_employee?.length ? (
            <div className="ll-scroll overflow-x-auto">
            <Table data-testid="employee-refund-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t("common.employee")}</TableHead>
                  <TableHead className="text-right">{t("common.receipts")}</TableHead>
                  <TableHead className="text-right">{t("common.gross")}</TableHead>
                  <TableHead className="text-right">{t("common.refund")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.by_employee.map((e) => (
                  <TableRow key={e.user_id || e.name}>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell className="ll-num text-right">{e.count}</TableCell>
                    <TableCell className="ll-num text-right">{formatMoney(e.gross, currency)}</TableCell>
                    <TableCell className="ll-num text-right text-primary">
                      {formatMoney(e.refund, currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {t("dash.noRefunds")}
            </p>
          )}
        </div>
        ) : null}
      </div>

      <ReceiptDetailDrawer
        receiptId={openId}
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        onChanged={() => {
          loadFeed(false);
          loadSummary();
          loadDuplicates();
        }}
        isAdmin={isAdmin}
        categories={categories}
        currency={currency}
      />
    </AdminShell>
  );
};

export default Dashboard;
